"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDropPollVotersPage } from "@/generated/models/ApiDropPollVotersPage";
import type { ApiIdentityOverview } from "@/generated/models/ApiIdentityOverview";
import { fetchDropPollOptionVotersV2 } from "@/services/api/wave-drops-v2-api";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
const DROP_POLL_OPTION_VOTERS_PAGE_SIZE = 20;
export const DROP_POLL_OPTION_VOTER_PREVIEW_PAGE_SIZE = 3;
const DROP_POLL_OPTION_VOTERS_STALE_TIME_MS = 60_000;
interface UseDropPollOptionVotersProps {
  readonly dropId: string;
  readonly optionNo: number | null;
  readonly enabled?: boolean | undefined;
  readonly pageSize?: number | undefined;
}
interface DropPollOptionVotersParams {
  readonly dropId: string;
  readonly optionNo: number | null;
  readonly pageSize?: number | undefined;
}
const getDropPollOptionVotersQueryKey = ({
  dropId,
  optionNo,
  pageSize = DROP_POLL_OPTION_VOTERS_PAGE_SIZE
}: DropPollOptionVotersParams) => [QueryKey.DROP_POLL_VOTERS, {
  dropId: dropId.trim(),
  optionNo,
  pageSize
}];
const getNextDropPollOptionVotersPageParam = (lastPage: ApiDropPollVotersPage, allPages: ApiDropPollVotersPage[]) => {
  if (!lastPage.next) {
    return null;
  }
  return typeof lastPage.page === "number" ? lastPage.page + 1 : allPages.length + 1;
};
const fetchDropPollOptionVotersPage = async ({
  dropId,
  optionNo,
  pageParam,
  pageSize = DROP_POLL_OPTION_VOTERS_PAGE_SIZE,
  signal
}: DropPollOptionVotersParams & {
  readonly pageParam: number;
  readonly signal?: AbortSignal | undefined;
}) => {
  if (optionNo === null) {
    throw new Error("Cannot fetch poll voters without an option number");
  }
  return await fetchDropPollOptionVotersV2({
    dropId: dropId.trim(),
    optionNo,
    page: pageParam,
    pageSize,
    signal
  });
};
export function usePrefetchDropPollOptionVoters() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = t1 => {
      const {
        dropId,
        optionNo,
        enabled: t2,
        pageSize: t3
      } = t1;
      const enabled = t2 === undefined ? true : t2;
      const pageSize = t3 === undefined ? DROP_POLL_OPTION_VOTERS_PAGE_SIZE : t3;
      const normalizedDropId = dropId.trim();
      if (!enabled || normalizedDropId.length === 0 || typeof optionNo !== "number") {
        return;
      }
      queryClient.prefetchInfiniteQuery({
        queryKey: getDropPollOptionVotersQueryKey({
          dropId: normalizedDropId,
          optionNo,
          pageSize
        }),
        queryFn: t4 => {
          const {
            pageParam,
            signal
          } = t4;
          return fetchDropPollOptionVotersPage({
            dropId: normalizedDropId,
            optionNo,
            pageParam,
            pageSize,
            signal
          });
        },
        initialPageParam: 1,
        getNextPageParam: getNextDropPollOptionVotersPageParam,
        pages: 1,
        staleTime: DROP_POLL_OPTION_VOTERS_STALE_TIME_MS,
        ...getDefaultQueryRetry()
      }).catch(_temp);
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp() {}
export function useDropPollOptionVoters(t0) {
  const $ = _c(25);
  const {
    dropId,
    optionNo,
    enabled: t1,
    pageSize: t2
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const pageSize = t2 === undefined ? DROP_POLL_OPTION_VOTERS_PAGE_SIZE : t2;
  let t3;
  if ($[0] !== dropId) {
    t3 = dropId.trim();
    $[0] = dropId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const normalizedDropId = t3;
  const canFetch = enabled && normalizedDropId.length > 0 && typeof optionNo === "number";
  let t4;
  if ($[2] !== normalizedDropId || $[3] !== optionNo || $[4] !== pageSize) {
    t4 = getDropPollOptionVotersQueryKey({
      dropId: normalizedDropId,
      optionNo,
      pageSize
    });
    $[2] = normalizedDropId;
    $[3] = optionNo;
    $[4] = pageSize;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const queryKey = t4;
  let t5;
  if ($[6] !== normalizedDropId || $[7] !== optionNo || $[8] !== pageSize) {
    t5 = t6 => {
      const {
        pageParam,
        signal
      } = t6;
      return fetchDropPollOptionVotersPage({
        dropId: normalizedDropId,
        optionNo,
        pageParam,
        pageSize,
        signal
      });
    };
    $[6] = normalizedDropId;
    $[7] = optionNo;
    $[8] = pageSize;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getDefaultQueryRetry();
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== canFetch || $[12] !== queryKey || $[13] !== t5) {
    t7 = {
      queryKey,
      queryFn: t5,
      initialPageParam: 1,
      getNextPageParam: getNextDropPollOptionVotersPageParam,
      placeholderData: keepPreviousData,
      enabled: canFetch,
      staleTime: DROP_POLL_OPTION_VOTERS_STALE_TIME_MS,
      ...t6
    };
    $[11] = canFetch;
    $[12] = queryKey;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const query = useInfiniteQuery(t7);
  let t8;
  if ($[15] !== query.data?.pages) {
    t8 = query.data?.pages.flatMap(_temp2) ?? [];
    $[15] = query.data?.pages;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const voters = t8;
  let t9;
  if ($[17] !== query.error || $[18] !== query.fetchNextPage || $[19] !== query.hasNextPage || $[20] !== query.isError || $[21] !== query.isFetchingNextPage || $[22] !== query.isLoading || $[23] !== voters) {
    t9 = {
      voters,
      fetchNextPage: query.fetchNextPage,
      hasNextPage: query.hasNextPage,
      isLoading: query.isLoading,
      isFetchingNextPage: query.isFetchingNextPage,
      isError: query.isError,
      error: query.error
    };
    $[17] = query.error;
    $[18] = query.fetchNextPage;
    $[19] = query.hasNextPage;
    $[20] = query.isError;
    $[21] = query.isFetchingNextPage;
    $[22] = query.isLoading;
    $[23] = voters;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function _temp2(page) {
  return page.data;
}